import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiPost } from '../lib/api';
import { getUserInfo } from '../auth/keycloak';
import { usePageVisibility } from './usePageVisibility';

/**
 * Custom hook that sends heartbeat to backend every 60 seconds
 * to indicate the user is still online/active in the browser.
 * 
 * Automatically pauses when tab is hidden and resumes when visible again.
 * Sends immediate heartbeat when tab becomes visible after being hidden.
 * 
 * This is more accurate than tracking API requests because:
 * - User might be reading content without making requests
 * - User might have the tab open but not interacting
 * - We want to track "browser open" not just "making requests"
 * 
 * Usage:
 *   useActivityHeartbeat(); // Call once in App.tsx or main layout
 */
export function useActivityHeartbeat() {
  const queryClient = useQueryClient();
  const isVisible = usePageVisibility();
  const wasVisibleRef = useRef(isVisible);

  useEffect(() => {
    const userInfo = getUserInfo();
    
    // Only send heartbeat if user is authenticated
    if (!userInfo) {
      return;
    }

    const onHeartbeatSuccess = () => {
      queryClient.invalidateQueries({ queryKey: ['online-users'] });
      queryClient.invalidateQueries({ queryKey: ['online-status'] });
    };

    // Send heartbeat when tab becomes visible
    if (isVisible && !wasVisibleRef.current) {
      sendHeartbeat(onHeartbeatSuccess);
    }
    wasVisibleRef.current = isVisible;

    // Only setup interval if tab is visible
    if (!isVisible) {
      return;
    }
    
    // Send initial heartbeat immediately
    sendHeartbeat(onHeartbeatSuccess);
    
    // Then send every 60 seconds while visible
    const intervalId = setInterval(() => {
      sendHeartbeat(onHeartbeatSuccess);
    }, 60000); // 60 seconds
    
    // Cleanup on unmount or when tab becomes hidden
    return () => {
      clearInterval(intervalId);
    };
  }, [isVisible, queryClient]);
}

async function sendHeartbeat(onSuccess?: () => void) {
  try {
    await apiPost('/api/activity/heartbeat', {});
    onSuccess?.();
  } catch (error) {
    // Silently fail - heartbeat errors shouldn't disrupt the user experience
    // The backend logs these errors if needed
    console.debug('Heartbeat failed (normal if offline):', error);
  }
}
